import { Preferences } from '@capacitor/preferences';
import axios from 'axios';

const DEVICE_ID_KEY = 'messenger-e2e-device-id';
const PRIVATE_JWK_KEY = 'messenger-e2e-private-jwk';
const PUBLIC_JWK_KEY = 'messenger-e2e-public-jwk';
const BACKUP_KEK_KEY = 'messenger-e2e-backup-kek';
const BACKUP_PROMPT_SEEN_KEY = 'messenger-e2e-backup-prompt-seen';
const BACKUP_PBKDF2_ITERATIONS = 210000;
const BACKUP_VERSION = 1;

const chatKeyCache = new Map<number, CryptoKey>();
const chatKeyMiss = new Set<number>();
let cryptoReadyPromise: Promise<{ deviceId: string; publicJwk: JsonWebKeyLocal }> | null = null;
let redistributeTimer: number | null = null;
let backupUploadTimer: number | null = null;
/** Разрешить генерацию новой identity (после skip restore или если бекапа нет). */
let allowIdentityCreate = false;
/** Кэш: есть ли бекап на сервере (для плейсхолдера decrypt). */
let serverBackupExistsCache: boolean | null = null;

type JsonWebKeyLocal = JsonWebKey;

type WrappedChatKeyPayload = {
  iv: string;
  data: string;
  /** ECDH-обёртка для чужого устройства */
  senderPublic?: string;
  /** AES-обёртка для своего устройства (без ECDH сам-с-собой) */
  self?: boolean;
};

export type MessengerKeyBackupBlob = {
  exists: boolean;
  updatedAt?: string;
  salt?: string;
  iv?: string;
  data?: string;
  kdf?: string;
  iterations?: number;
  version?: number;
};

type BackupPlainPayload = {
  v: number;
  deviceId: string;
  publicJwk: string;
  privateJwk: string;
  chatKeys: Record<string, string>;
};

function chatKeyStorageKey(chatId: number): string {
  return `messenger-e2e-chat-key-${chatId}`;
}

function isValidChatId(chatId: unknown): chatId is number {
  return typeof chatId === 'number' && Number.isFinite(chatId) && chatId !== 0;
}

function b64Encode(bytes: ArrayBuffer | Uint8Array): string {
  const arr = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let binary = '';
  arr.forEach(b => {
    binary += String.fromCharCode(b);
  });
  return btoa(binary);
}

function b64Decode(value: string): ArrayBuffer {
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

async function importPublicKey(jwk: JsonWebKeyLocal): Promise<CryptoKey> {
  return crypto.subtle.importKey('jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, true, []);
}

async function importPrivateKey(jwk: JsonWebKeyLocal): Promise<CryptoKey> {
  return crypto.subtle.importKey('jwk', jwk, { name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
}

async function deriveWrapKey(privateKey: CryptoKey, publicKey: CryptoKey): Promise<CryptoKey> {
  const bits = await crypto.subtle.deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256);
  return crypto.subtle.importKey('raw', bits, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

/** Обёртка ключа чата для своего устройства: ECDH(priv, ownPub) в WebCrypto часто падает. */
async function deriveSelfWrapKey(privateJwkRaw: string): Promise<CryptoKey> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`liga-e2e-self:${privateJwkRaw}`));
  return crypto.subtle.importKey('raw', digest, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
}

async function importChatKeyRaw(raw: ArrayBuffer): Promise<CryptoKey> {
  // extractable: true — иначе после reload нельзя exportKey для redistribute на новые устройства.
  return crypto.subtle.importKey('raw', raw, { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
}

async function cacheChatKey(chatId: number, key: CryptoKey): Promise<void> {
  chatKeyCache.set(chatId, key);
  try {
    const raw = await crypto.subtle.exportKey('raw', key);
    await Preferences.set({ key: chatKeyStorageKey(chatId), value: b64Encode(raw) });
  } catch (error) {
    console.warn('Failed to persist chat key locally', error);
  }
  scheduleUploadKeyBackup();
}

async function loadCachedChatKey(chatId: number): Promise<CryptoKey | null> {
  const mem = chatKeyCache.get(chatId);
  if (mem) return mem;
  const stored = (await Preferences.get({ key: chatKeyStorageKey(chatId) })).value;
  if (!stored) return null;
  try {
    const key = await importChatKeyRaw(b64Decode(stored));
    chatKeyCache.set(chatId, key);
    return key;
  } catch {
    return null;
  }
}

export async function hasLocalMessengerIdentity(): Promise<boolean> {
  const deviceId = (await Preferences.get({ key: DEVICE_ID_KEY })).value;
  const publicRaw = (await Preferences.get({ key: PUBLIC_JWK_KEY })).value;
  const privateRaw = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;
  return !!(deviceId && publicRaw && privateRaw);
}

/** Разрешить генерацию новой identity (нет бекапа или пользователь пропустил restore). */
export function allowMessengerIdentityCreate(): void {
  allowIdentityCreate = true;
}

export function resetMessengerCryptoReady(): void {
  cryptoReadyPromise = null;
}

export async function ensureMessengerCryptoReady(): Promise<{ deviceId: string; publicJwk: JsonWebKeyLocal }> {
  if (!cryptoReadyPromise) {
    cryptoReadyPromise = initMessengerCrypto().catch(error => {
      cryptoReadyPromise = null;
      throw error;
    });
  }
  return cryptoReadyPromise;
}

async function initMessengerCrypto(): Promise<{ deviceId: string; publicJwk: JsonWebKeyLocal }> {
  let deviceId = (await Preferences.get({ key: DEVICE_ID_KEY })).value;
  let publicRaw = (await Preferences.get({ key: PUBLIC_JWK_KEY })).value;
  let privateRaw = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;

  if (!deviceId || !publicRaw || !privateRaw) {
    if (!allowIdentityCreate) {
      throw new Error('MESSENGER_IDENTITY_PENDING');
    }
    deviceId =
      typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `dev-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const pair = await crypto.subtle.generateKey({ name: 'ECDH', namedCurve: 'P-256' }, true, ['deriveBits']);
    const publicJwk = await crypto.subtle.exportKey('jwk', pair.publicKey);
    const privateJwk = await crypto.subtle.exportKey('jwk', pair.privateKey);
    publicRaw = JSON.stringify(publicJwk);
    privateRaw = JSON.stringify(privateJwk);
    await Preferences.set({ key: DEVICE_ID_KEY, value: deviceId });
    await Preferences.set({ key: PUBLIC_JWK_KEY, value: publicRaw });
    await Preferences.set({ key: PRIVATE_JWK_KEY, value: privateRaw });
  }

  const publicJwk = JSON.parse(publicRaw) as JsonWebKeyLocal;
  await axios.post('api/messenger/devices', {
    deviceId,
    identityKey: publicRaw,
  });
  return { deviceId, publicJwk };
}

async function loadPrivateKey(): Promise<CryptoKey> {
  const privateRaw = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;
  if (!privateRaw) throw new Error('E2E private key missing');
  return importPrivateKey(JSON.parse(privateRaw));
}

async function exportChatKeyRaw(chatId: number, chatKey: CryptoKey): Promise<ArrayBuffer> {
  try {
    return await crypto.subtle.exportKey('raw', chatKey);
  } catch (error) {
    const stored = (await Preferences.get({ key: chatKeyStorageKey(chatId) })).value;
    if (stored) return b64Decode(stored);
    throw error;
  }
}

async function wrapChatKeyForDevice(params: {
  chatId: number;
  chatKey: CryptoKey;
  privateKey: CryptoKey;
  privateJwkRaw: string;
  myDeviceId: string;
  myPublicRaw: string;
  device: { userId: number; deviceId: string; wrappedKey: string };
}): Promise<{ userId: number; deviceId: string; wrappedKey: string }> {
  const rawChat = await exportChatKeyRaw(params.chatId, params.chatKey);
  const iv = crypto.getRandomValues(new Uint8Array(12));

  if (params.device.deviceId === params.myDeviceId) {
    const wrapKey = await deriveSelfWrapKey(params.privateJwkRaw);
    const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrapKey, rawChat);
    return {
      userId: params.device.userId,
      deviceId: params.device.deviceId,
      wrappedKey: JSON.stringify({
        self: true,
        iv: b64Encode(iv),
        data: b64Encode(encrypted),
      } as WrappedChatKeyPayload),
    };
  }

  const theirJwk = (() => {
    try {
      return JSON.parse(params.device.wrappedKey) as JsonWebKeyLocal;
    } catch {
      return null;
    }
  })();
  if (!theirJwk?.kty || !theirJwk.x || !theirJwk.y) {
    throw new Error('Device identity key is not a valid JWK');
  }
  const theirPub = await importPublicKey(theirJwk);
  const wrapKey = await deriveWrapKey(params.privateKey, theirPub);
  const encrypted = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrapKey, rawChat);
  return {
    userId: params.device.userId,
    deviceId: params.device.deviceId,
    wrappedKey: JSON.stringify({
      iv: b64Encode(iv),
      data: b64Encode(encrypted),
      senderPublic: params.myPublicRaw,
    } as WrappedChatKeyPayload),
  };
}

async function unwrapChatKey(wrappedPayload: string): Promise<CryptoKey> {
  const payload = JSON.parse(wrappedPayload) as WrappedChatKeyPayload;
  if (payload.self) {
    const privateRaw = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;
    if (!privateRaw) throw new Error('E2E private key missing');
    const wrapKey = await deriveSelfWrapKey(privateRaw);
    const raw = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: b64Decode(payload.iv) },
      wrapKey,
      b64Decode(payload.data)
    );
    return importChatKeyRaw(raw);
  }
  if (!payload.senderPublic) throw new Error('E2E wrapped key missing senderPublic');
  const privateKey = await loadPrivateKey();
  const senderPub = await importPublicKey(JSON.parse(payload.senderPublic));
  const wrapKey = await deriveWrapKey(privateKey, senderPub);
  const raw = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: b64Decode(payload.iv) },
    wrapKey,
    b64Decode(payload.data)
  );
  return importChatKeyRaw(raw);
}

async function loadExistingChatKey(chatId: number, force = false): Promise<CryptoKey | null> {
  if (!isValidChatId(chatId)) return null;
  const cached = await loadCachedChatKey(chatId);
  if (cached) {
    chatKeyMiss.delete(chatId);
    return cached;
  }
  if (!force && chatKeyMiss.has(chatId)) return null;

  try {
    const { deviceId } = await ensureMessengerCryptoReady();
    const { data: myKeys } = await axios.get<Array<{ wrappedKey: string; deviceId: string }>>(
      `api/messenger/chats/${chatId}/keys`,
      { params: { deviceId } }
    );
    const mine = (myKeys || []).find(item => item.deviceId === deviceId);
    if (!mine?.wrappedKey) {
      chatKeyMiss.add(chatId);
      return null;
    }
    const key = await unwrapChatKey(mine.wrappedKey);
    await cacheChatKey(chatId, key);
    chatKeyMiss.delete(chatId);
    return key;
  } catch (error) {
    console.warn('Load chat key failed', chatId, error);
    chatKeyMiss.add(chatId);
    return null;
  }
}

export function invalidateChatKeyMiss(chatId?: number): void {
  if (chatId == null) chatKeyMiss.clear();
  else chatKeyMiss.delete(chatId);
}

/** Есть ли уже обёртки ключа чата у текущего пользователя (на любом устройстве). */
async function userAlreadyHasChatKey(chatId: number): Promise<boolean> {
  const { data: myKeys } = await axios.get<Array<{ wrappedKey: string; deviceId: string }>>(
    `api/messenger/chats/${chatId}/keys`
  );
  return (myKeys || []).some(item => !!item.wrappedKey);
}

/**
 * Раздаёт уже известный chat key на все текущие device-keys участников.
 * Нужно, когда новое устройство (напр. web) зарегистрировалось после создания ключа.
 */
async function redistributeChatKey(chatId: number, chatKey: CryptoKey): Promise<void> {
  if (!isValidChatId(chatId)) return;
  const { deviceId: myDeviceId } = await ensureMessengerCryptoReady();
  const privateJwkRaw = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;
  const myPublicRaw = (await Preferences.get({ key: PUBLIC_JWK_KEY })).value;
  if (!privateJwkRaw || !myPublicRaw) return;

  const { data: devices } = await axios.get<Array<{ userId: number; deviceId: string; wrappedKey: string }>>(
    `api/messenger/chats/${chatId}/device-keys`
  );
  if (!devices?.length) return;

  const privateKey = await loadPrivateKey();
  const keys: Array<{ userId: number; deviceId: string; wrappedKey: string }> = [];
  for (const device of devices) {
    try {
      keys.push(
        await wrapChatKeyForDevice({
          chatId,
          chatKey,
          privateKey,
          privateJwkRaw,
          myDeviceId,
          myPublicRaw,
          device,
        })
      );
    } catch (error) {
      console.warn('Skip redistributing chat key for device', device.deviceId, error);
    }
  }
  if (!keys.length) {
    console.warn('Chat key redistribute produced no wraps', { chatId, deviceCount: devices.length });
    return;
  }
  await axios.put(`api/messenger/chats/${chatId}/keys`, { keys });
}

/**
 * Если на этом устройстве есть ключ — переобернуть для новых устройств участников.
 * Если ключа нет — попробовать скачать обёртку (после sync с другого устройства).
 */
export async function syncChatKeyForDevices(chatId: number): Promise<CryptoKey | null> {
  if (!isValidChatId(chatId)) return null;
  const existing = await loadExistingChatKey(chatId);
  if (existing) {
    await redistributeChatKey(chatId, existing).catch(error => {
      console.warn('Chat key redistribute failed', error);
    });
    return existing;
  }
  return null;
}

/** Только чаты, ключ которых уже лежит на ЭТОМ устройстве — без обхода всего инбокса. */
export async function redistributeKeysForKnownChats(): Promise<void> {
  const ids = new Set<number>(chatKeyCache.keys());
  try {
    const { keys } = await Preferences.keys();
    const prefix = 'messenger-e2e-chat-key-';
    for (const key of keys || []) {
      if (!key.startsWith(prefix)) continue;
      const id = Number(key.slice(prefix.length));
      if (Number.isFinite(id) && id !== 0) ids.add(id);
    }
  } catch {
    /* ignore */
  }
  if (!ids.size) return;
  for (const chatId of ids) {
    if (!isValidChatId(chatId)) continue;
    await syncChatKeyForDevices(chatId).catch(error => {
      console.warn('Redistribute chat key failed', chatId, error);
    });
  }
}

export function scheduleRedistributeKnownChatKeys(): void {
  if (redistributeTimer != null) return;
  redistributeTimer = window.setTimeout(() => {
    redistributeTimer = null;
    void redistributeKeysForKnownChats().catch(() => undefined);
  }, 600);
}

/** Повторно скачать wrap после CHAT_KEYS_UPDATED — без повторной раздачи. */
export async function refreshChatKeyFromServer(chatId: number): Promise<CryptoKey | null> {
  invalidateChatKeyMiss(chatId);
  return loadExistingChatKey(chatId, true);
}

export async function ensureChatKey(chatId: number): Promise<CryptoKey> {
  if (!isValidChatId(chatId)) {
    throw new Error('Некорректный chatId для ключа шифрования');
  }
  const existing = await loadExistingChatKey(chatId);
  if (existing) {
    // Новые устройства того же аккаунта / новых участников получают обёртку.
    await redistributeChatKey(chatId, existing).catch(error => {
      console.warn('Chat key redistribute failed', error);
    });
    return existing;
  }

  // Ключ уже есть на другом устройстве — нельзя генерировать новый (старые сообщения станут нечитаемыми).
  if (await userAlreadyHasChatKey(chatId)) {
    throw new Error(
      'CHAT_KEY_ON_OTHER_DEVICE: откройте чат на устройстве, где уже есть ключ, чтобы синхронизировать web'
    );
  }

  const { deviceId: myDeviceId } = await ensureMessengerCryptoReady();
  const privateJwkRaw = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;
  const myPublicRaw = (await Preferences.get({ key: PUBLIC_JWK_KEY })).value;
  if (!privateJwkRaw || !myPublicRaw) throw new Error('E2E keys missing');

  const chatKey = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt']);
  const { data: devices } = await axios.get<Array<{ userId: number; deviceId: string; wrappedKey: string }>>(
    `api/messenger/chats/${chatId}/device-keys`
  );
  const privateKey = await loadPrivateKey();
  const keys: Array<{ userId: number; deviceId: string; wrappedKey: string }> = [];
  let wrappedForSelf = false;

  for (const device of devices || []) {
    try {
      const wrapped = await wrapChatKeyForDevice({
        chatId,
        chatKey,
        privateKey,
        privateJwkRaw,
        myDeviceId,
        myPublicRaw,
        device,
      });
      keys.push(wrapped);
      if (device.deviceId === myDeviceId) wrappedForSelf = true;
    } catch (error) {
      console.warn('Skip wrapping chat key for device', device.deviceId, error);
    }
  }

  if (!wrappedForSelf) {
    throw new Error('Не удалось сохранить ключ шифрования для этого устройства');
  }
  if (keys.length) {
    await axios.put(`api/messenger/chats/${chatId}/keys`, { keys });
  }
  await cacheChatKey(chatId, chatKey);
  return chatKey;
}

export async function encryptMessageBody(chatId: number, body: string): Promise<string> {
  const key = await ensureChatKey(chatId);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encoded = new TextEncoder().encode(body || '');
  const cipher = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, encoded);
  return JSON.stringify({ iv: b64Encode(iv), data: b64Encode(cipher) });
}

export async function decryptMessageBody(chatId: number, ciphertext: string | null | undefined): Promise<string | null> {
  if (!ciphertext) return null;
  if (!isValidChatId(chatId)) {
    return '🔒 Нет ключа на этом устройстве. Откройте Liga.Chat там, где сообщения уже читались — браузер получит ключ сам.';
  }
  try {
    const payload = JSON.parse(ciphertext) as { iv: string; data: string };
    const key = await loadExistingChatKey(chatId);
    if (!key) {
      if (serverBackupExistsCache) {
        return '🔒 Нет ключа на этом устройстве. Введите код восстановления Liga.Chat в настройках приватности или при запросе при входе.';
      }
      return '🔒 Нет ключа на этом устройстве. Откройте Liga.Chat там, где сообщения уже читались — браузер получит ключ сам.';
    }
    const plain = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: b64Decode(payload.iv) },
      key,
      b64Decode(payload.data)
    );
    return new TextDecoder().decode(plain);
  } catch (error) {
    console.warn('E2E decrypt failed', error);
    return '🔒 Не удалось расшифровать';
  }
}

export function isE2EChatType(type?: string): boolean {
  return type === 'DIRECT' || type === 'GROUP' || type === 'PUBLIC';
}

// ——— Бекап ключей (код из 6 цифр) ———

export function generateRecoveryCode(): string {
  const n = 100000 + Math.floor(Math.random() * 900000);
  return String(n);
}

export function isValidRecoveryCode(code: string): boolean {
  return /^\d{6}$/.test((code || '').trim());
}

export async function isKeyBackupPromptSeen(): Promise<boolean> {
  return (await Preferences.get({ key: BACKUP_PROMPT_SEEN_KEY })).value === '1';
}

export async function markKeyBackupPromptSeen(): Promise<void> {
  await Preferences.set({ key: BACKUP_PROMPT_SEEN_KEY, value: '1' });
}

export function setServerKeyBackupExists(exists: boolean): void {
  serverBackupExistsCache = exists;
}

export function getServerKeyBackupExists(): boolean | null {
  return serverBackupExistsCache;
}

async function deriveBackupKek(code: string, salt: ArrayBuffer): Promise<CryptoKey> {
  const baseKey = await crypto.subtle.importKey('raw', new TextEncoder().encode(code), 'PBKDF2', false, ['deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt, iterations: BACKUP_PBKDF2_ITERATIONS, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );
}

async function storeBackupKek(kek: CryptoKey): Promise<void> {
  const raw = await crypto.subtle.exportKey('raw', kek);
  await Preferences.set({ key: BACKUP_KEK_KEY, value: b64Encode(raw) });
}

async function loadStoredBackupKek(): Promise<CryptoKey | null> {
  const stored = (await Preferences.get({ key: BACKUP_KEK_KEY })).value;
  if (!stored) return null;
  try {
    return await crypto.subtle.importKey('raw', b64Decode(stored), { name: 'AES-GCM' }, true, ['encrypt', 'decrypt']);
  } catch {
    return null;
  }
}

async function collectLocalChatKeys(): Promise<Record<string, string>> {
  const chatKeys: Record<string, string> = {};
  try {
    const { keys } = await Preferences.keys();
    const prefix = 'messenger-e2e-chat-key-';
    for (const key of keys || []) {
      if (!key.startsWith(prefix)) continue;
      const id = key.slice(prefix.length);
      const value = (await Preferences.get({ key })).value;
      if (value) chatKeys[id] = value;
    }
  } catch {
    /* ignore */
  }
  for (const [chatId, cryptoKey] of chatKeyCache.entries()) {
    if (chatKeys[String(chatId)]) continue;
    try {
      const raw = await crypto.subtle.exportKey('raw', cryptoKey);
      chatKeys[String(chatId)] = b64Encode(raw);
    } catch {
      /* ignore */
    }
  }
  return chatKeys;
}

async function buildBackupPlainPayload(): Promise<BackupPlainPayload> {
  const deviceId = (await Preferences.get({ key: DEVICE_ID_KEY })).value;
  const publicJwk = (await Preferences.get({ key: PUBLIC_JWK_KEY })).value;
  const privateJwk = (await Preferences.get({ key: PRIVATE_JWK_KEY })).value;
  if (!deviceId || !publicJwk || !privateJwk) {
    throw new Error('Нет локальных ключей для бекапа');
  }
  return {
    v: BACKUP_VERSION,
    deviceId,
    publicJwk,
    privateJwk,
    chatKeys: await collectLocalChatKeys(),
  };
}

async function putEncryptedBackup(params: {
  salt: string;
  iv: string;
  data: string;
  kdf: string;
  iterations: number;
  version: number;
}): Promise<void> {
  await axios.put('api/messenger/key-backup', params);
  serverBackupExistsCache = true;
}

export async function createKeyBackup(code: string): Promise<void> {
  const trimmed = (code || '').trim();
  if (!isValidRecoveryCode(trimmed)) throw new Error('Код должен состоять из 6 цифр');
  if (!(await hasLocalMessengerIdentity())) throw new Error('Нет локальных ключей');

  const salt = crypto.getRandomValues(new Uint8Array(16));
  const kek = await deriveBackupKek(trimmed, salt.buffer);
  const plain = await buildBackupPlainPayload();
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const encrypted = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv },
    kek,
    new TextEncoder().encode(JSON.stringify(plain))
  );
  await putEncryptedBackup({
    salt: b64Encode(salt),
    iv: b64Encode(iv),
    data: b64Encode(encrypted),
    kdf: 'PBKDF2',
    iterations: BACKUP_PBKDF2_ITERATIONS,
    version: BACKUP_VERSION,
  });
  await storeBackupKek(kek);
  await markKeyBackupPromptSeen();
}

export async function changeKeyBackupCode(oldCode: string, newCode: string): Promise<void> {
  const oldTrimmed = (oldCode || '').trim();
  const newTrimmed = (newCode || '').trim();
  if (!isValidRecoveryCode(oldTrimmed) || !isValidRecoveryCode(newTrimmed)) {
    throw new Error('Код должен состоять из 6 цифр');
  }
  // Проверяем старый код расшифровкой текущего blob
  const { data: blob } = await axios.get<MessengerKeyBackupBlob>('api/messenger/key-backup');
  if (!blob?.exists || !blob.salt || !blob.iv || !blob.data) {
    throw new Error('Бекап на сервере не найден');
  }
  const oldKek = await deriveBackupKek(oldTrimmed, b64Decode(blob.salt));
  try {
    await crypto.subtle.decrypt({ name: 'AES-GCM', iv: b64Decode(blob.iv) }, oldKek, b64Decode(blob.data));
  } catch {
    throw new Error('Неверный текущий код');
  }
  await createKeyBackup(newTrimmed);
}

export async function restoreKeyBackup(code: string, blob?: MessengerKeyBackupBlob): Promise<void> {
  const trimmed = (code || '').trim();
  if (!isValidRecoveryCode(trimmed)) throw new Error('Код должен состоять из 6 цифр');

  let payload = blob;
  if (!payload?.exists) {
    const { data } = await axios.get<MessengerKeyBackupBlob>('api/messenger/key-backup');
    payload = data;
  }
  if (!payload?.exists || !payload.salt || !payload.iv || !payload.data) {
    throw new Error('Бекап на сервере не найден');
  }

  const iterations = payload.iterations || BACKUP_PBKDF2_ITERATIONS;
  const baseKey = await crypto.subtle.importKey('raw', new TextEncoder().encode(trimmed), 'PBKDF2', false, ['deriveKey']);
  const kek = await crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: b64Decode(payload.salt), iterations, hash: 'SHA-256' },
    baseKey,
    { name: 'AES-GCM', length: 256 },
    true,
    ['encrypt', 'decrypt']
  );

  let plainRaw: ArrayBuffer;
  try {
    plainRaw = await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: b64Decode(payload.iv) },
      kek,
      b64Decode(payload.data)
    );
  } catch {
    throw new Error('Неверный код восстановления');
  }

  const plain = JSON.parse(new TextDecoder().decode(plainRaw)) as BackupPlainPayload;
  if (!plain?.deviceId || !plain.publicJwk || !plain.privateJwk) {
    throw new Error('Бекап повреждён');
  }

  await Preferences.set({ key: DEVICE_ID_KEY, value: plain.deviceId });
  await Preferences.set({ key: PUBLIC_JWK_KEY, value: plain.publicJwk });
  await Preferences.set({ key: PRIVATE_JWK_KEY, value: plain.privateJwk });
  await storeBackupKek(kek);

  chatKeyCache.clear();
  chatKeyMiss.clear();
  if (plain.chatKeys) {
    for (const [chatIdStr, rawB64] of Object.entries(plain.chatKeys)) {
      const chatId = Number(chatIdStr);
      if (!Number.isFinite(chatId) || !rawB64) continue;
      await Preferences.set({ key: chatKeyStorageKey(chatId), value: rawB64 });
      try {
        const key = await importChatKeyRaw(b64Decode(rawB64));
        chatKeyCache.set(chatId, key);
      } catch {
        /* ignore */
      }
    }
  }

  serverBackupExistsCache = true;
  resetMessengerCryptoReady();
  await ensureMessengerCryptoReady();
  scheduleRedistributeKnownChatKeys();
}

/** Тихий re-upload, если локально есть KEK от кода восстановления. */
export async function uploadKeyBackupIfPossible(): Promise<void> {
  const kek = await loadStoredBackupKek();
  if (!kek) return;
  if (!(await hasLocalMessengerIdentity())) return;

  try {
    const { data: existing } = await axios.get<MessengerKeyBackupBlob>('api/messenger/key-backup');
    if (!existing?.exists || !existing.salt || !existing.iv) {
      // KEK есть, но blob нет — не можем пересоздать без кода (соль нужна из blob).
      // Перешифруем с новой солью нельзя без кода. Просто выходим.
      return;
    }
    // Перешифровываем тем же KEK, но нужна та же соль? KEK derived from salt+code.
    // Храним только KEK — можем шифровать новым IV той же KEK, но соль на сервере должна совпадать с derive.
    // Поэтому оставляем salt/iterations/kdf/version как в существующем blob.
    const plain = await buildBackupPlainPayload();
    const iv = crypto.getRandomValues(new Uint8Array(12));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv },
      kek,
      new TextEncoder().encode(JSON.stringify(plain))
    );
    await putEncryptedBackup({
      salt: existing.salt,
      iv: b64Encode(iv),
      data: b64Encode(encrypted),
      kdf: existing.kdf || 'PBKDF2',
      iterations: existing.iterations || BACKUP_PBKDF2_ITERATIONS,
      version: existing.version || BACKUP_VERSION,
    });
  } catch (error) {
    console.warn('Key backup upload failed', error);
  }
}

export function scheduleUploadKeyBackup(): void {
  if (backupUploadTimer != null) return;
  backupUploadTimer = window.setTimeout(() => {
    backupUploadTimer = null;
    void uploadKeyBackupIfPossible().catch(() => undefined);
  }, 1500);
}

/** Нужно ли показать промпт создания бекапа после первого E2E-чата. */
export async function shouldPromptCreateKeyBackup(keyBackupEnabled?: boolean): Promise<boolean> {
  if (keyBackupEnabled) return false;
  if (await isKeyBackupPromptSeen()) return false;
  if (!(await hasLocalMessengerIdentity())) return false;
  return true;
}
